import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, Optional, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, filter, distinctUntilChanged, debounceTime, switchMap } from 'rxjs/operators';
import { DataBindingService } from '../../services/data-binding.service';
import { FakeDataGeneratorService } from '../../services/fake-data-generator.service';
import { MasterDetailService } from '../../services/master-detail.service';
import { DataSyncService } from '../../services/data-sync.service';
import { LEGACY_SCREEN_STATE, LegacyScreenState } from '../legacy-screen-state';
import { TranslateTAPipe } from '../../render/translate-ta.pipe';
// O MESMO formato do modulo antigo, ja declarado na ponte e com teste de ida
// e volta. Importar de la evita trazer o arquivo de interfaces de 844 linhas.
import { LegacyGridColumn as DataGridColumn } from '../../render/bridge/grid-columns';
import { HTTPService } from '../../../services/http.service';

/**
 * Componente DataGrid - Grade de dados com suporte completo
 * 
 * Funcionalidades:
 * - Exibição de dados baseada em JSON path
 * - Paginação (client-side)
 * - Ordenação por colunas
 * - Filtros por coluna
 * - Seleção de linhas (única ou múltipla)
 * - Edição inline (opcional)
 * - CRUD (opcional)
 */
/** Sem estado de construtor, nao ha painel para consultar. */
function emptyIds(): Set<string> {
  return new Set<string>();
}

@Component({
  selector: 'tem-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCheckboxModule,
    NzDatePickerModule,
    NzEmptyModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzSelectModule,
    NzSwitchModule,
    NzTableModule,
    NzTagModule,
    NzToolTipModule,
    TranslateTAPipe
  ],
  // OnPush. O original ficou em `Default` com o comentário "TEMPORÁRIO: mudado
  // de OnPush para Default para debug de sincronização" — um debug que ficou.
  // Dentro de um pai OnPush isso é o pior dos dois mundos: a grade recalcula a
  // cada ciclo global E ainda assim depende do pai marcar. O componente já chama
  // `cdr.detectChanges()` nos pontos em que precisa.
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataGridComponent implements OnInit, OnChanges, OnDestroy {
  /** Intervalo mínimo entre chamadas de loadData (ms) para evitar loops */
  private static readonly MIN_LOAD_INTERVAL_MS = 100;
  // ✅ Aceitar props como objeto único
  @Input() props: any;

  /**
   * Path efetivo do array quando a grade está dentro de uma aba (ex: response.magalu.channels).
   * Se informado, substitui props.dataPath para isNestedGrid, loadNestedData e seleção.
   * Usado pelo canvas quando a grade filha está em uma tab e o node tem só o path do pai.
   */
  @Input() effectiveDataPath?: string;

  /** Indica que esta grade está dentro de uma aba; usado para inferir path aninhado na injeção de dados. */
  @Input() isTabGrid = false;

  // ✅ NOVO: Preview Mode (ativa dados fake)
  @Input() previewMode = false;

  /** Titulo exibido acima da grade. Vazio esconde o cabecalho inteiro. */
  label = '';
  @Input() previewData?: any[]; // Dados específicos para preview
  
  // ✅ NOVO: Output para emitir linha selecionada (Master-Detail)
  @Output() rowSelected = new EventEmitter<any>();

  /** Emite quando o estado flip muda (true=formulário visível, false=grade visível) */
  @Output() flipStateChanged = new EventEmitter<{ active: boolean; gridId: string; panelIds: string[] }>();

  /** Emite quando o drawer deve abrir/fechar */
  @Output() drawerStateChanged = new EventEmitter<{ active: boolean; gridId: string; panelIds: string[]; drawerWidth: number }>();
  
  // Propriedades extraídas de props
  columns: DataGridColumn[] = [];
  dataPath?: string;
  enablePagination = true;
  pageSize = 10;
  enableFilters = false;
  enableSorting = true;
  enableCRUD = false;
  selectionMode: 'single' | 'multiple' | 'none' = 'none';
  enableMasterDetail = false;
  masterDetailFormPanel?: string;
  masterDetailFormPanels?: string[];
  maxVisibleRows = 0; // 0 = sem limite de linhas visíveis
  viewMode: 'inline' | 'drawer' | 'flip' | 'spreadsheet' = 'inline';
  drawerWidth = 40;

  /** Estado do modo flip: indica se os formulários estão visíveis no lugar da grade */
  isFlipEditing = false;
  /** Indica se o flip é para adição ou edição */
  flipEditingMode: 'adding' | 'editing' = 'editing';

  /** Estado do modo drawer: indica se a gaveta está aberta */
  isDrawerOpen = false;

  /** Modo spreadsheet: célula atualmente em edição */
  activeCell: { rowIndex: number; colIndex: number } | null = null;
  /** Flag local para evitar re-render durante edição spreadsheet (R3: NÃO usar isCellEditing global) */
  private _isSpreadsheetEditing = false;

  // Configuração de scroll calculada dinamicamente
  scrollConfig: { x: string; y?: string } = { x: 'max-content' };
  
  originalData: any[] = [];
  displayData: any[] = [];
  
  /** Flag para forçar re-renderização da tabela */
  tableVisible = true;
  
  currentPage = 1;
  totalPages = 1;
  
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  
  columnFilters: Record<string, string> = {};
  
  selectedRows: Set<any> = new Set();
  /** Evita piscar a grade na inicialização com auto-add + flip/drawer */
  public suppressGridFaceForInitialAutoAdd = false;
  
  private hasAutoSelected = false;
  /** Evita reabrir formulário repetidamente após auto-add inicial */
  private hasAutoOpenedFormOnInitialAdd = false;
  private _initialAutoAddSuppressTimer: ReturnType<typeof setTimeout> | null = null;

  /** Path efetivo quando esta grade exibe dados aninhados (ex: aba filha); usado ao atualizar currentSelectionSubject. */
  private _lastEffectivePath?: string;

  /** Hash do array de dados para detectar mudanças reais (evita reprocessamento desnecessário) */
  private _lastDataHash: string | null = null;

  /** Flag para indicar que estamos no meio de uma atualização (evita loops) */
  private _isProcessingUpdate = false;

  /** Timestamp da última chamada de loadData (para throttle) */
  private _lastLoadTimestamp = 0;

  /** Timer pendente de loadData (para evitar múltiplos setTimeouts em fila) */
  private _pendingLoadDataTimer: ReturnType<typeof setTimeout> | null = null;

  /** Timestamp da última chamada de loadNestedData (para debounce) */
  private _lastNestedLoadTimestamp = 0;

  /** Timer pendente de loadNestedData trailing edge (garante que última chamada debounced seja processada) */
  private _pendingNestedLoadTimer: ReturnType<typeof setTimeout> | null = null;

  /** Índice do item pai usado na última chamada de loadNestedData (evita recarregar mesmo índice) */
  private _lastNestedParentIndex = -1;

  private destroy$ = new Subject<void>();

  /** Subject para debounce de edição de célula (evita múltiplas chamadas enquanto usuário digita) */
  private cellEditSubject$ = new Subject<{ row: any; column: DataGridColumn; value: any; displayIndex: number }>();

  /** Timer ID para onCellBlur (cancelar timer anterior quando novo blur acontece) */
  private _cellBlurTimerId: ReturnType<typeof setTimeout> | null = null;

  /** Flag temporária para applyFiltersAndSort pular destruição/recriação da tabela */
  private _skipTableRecreation = false;

  /** Flag: ao próximo loadNestedData que auto-selecionar, escolher a ÚLTIMA linha (recém-adicionada) */
  private _selectLastRowOnNextLoad = false;

  // Search column: cache de valores resolvidos (key -> display label)
  searchDisplayCache: Map<string, Map<any, string>> = new Map();
  // Search column: opções carregadas por coluna (para o popover/select inline)
  searchColumnOptions: Map<string, Array<{ key: any; value: string }>> = new Map();
  searchColumnLoading: Map<string, boolean> = new Map();
  private searchColumnSubjects: Map<string, Subject<string>> = new Map();

  constructor(
    private dataBindingService: DataBindingService,
    private fakeDataGenerator: FakeDataGeneratorService,
    private masterDetailService: MasterDetailService,
    private dataSyncService: DataSyncService,
    // Por TOKEN, e opcional: a grade usa dois membros do estado do construtor,
    // e fora de um contexto de tela ela funciona sem mestre-detalhe em vez de
    // estourar. Ver `legacy-screen-state.ts`.
    @Optional() @Inject(LEGACY_SCREEN_STATE) private builderService: LegacyScreenState | null,
    private cdr: ChangeDetectorRef,
    private httpService: HTTPService
  ) {}

  /** Flag para evitar inscrição duplicada em subscribeToParentSelectionChange */
  private _hasSubscribedToParentSelection = false;

  ngOnChanges(changes: SimpleChanges): void {
    // 🔄 Detectar mudanças nas props e re-extrair propriedades
    if (changes['props'] && this.props) {
      this.updateFromProps();
    }

    // NOVO: Quando effectiveDataPath ou isTabGrid mudam,
    // verificar se precisa inscrever para mudanças do pai
    // Ambos precisam estar definidos para a verificação funcionar
    if ((changes['effectiveDataPath'] || changes['isTabGrid']) && !this._hasSubscribedToParentSelection) {
      // Agendar verificação após ciclo de detecção de mudanças
      // para garantir que todos os inputs foram processados
      setTimeout(() => {
        if (!this._hasSubscribedToParentSelection) {
          this.checkAndSubscribeToParentSelection();
        }
      }, 0);
    }
  }

  ngOnInit() {
    // ✅ Configurar processamento de edição de célula COM debounce reduzido
    // CORREÇÃO: Debounce muito longo (300ms) causava problemas - dados eram sobrescritos
    // antes de serem salvos. Reduzido para 50ms (suficiente para agrupar digitação rápida)
    this.cellEditSubject$.pipe(
      takeUntil(this.destroy$),
      debounceTime(50)
    ).subscribe(({ row, column, value, displayIndex }) => {
      this.processCellEdit(row, column, value, displayIndex);
    });

    // ✅ Extrair propriedades de props na inicialização
    if (this.props) {
      this.updateFromProps();
    }

    // Observar mudanças nos dados
    this.subscribeToDataChanges();

    // NOVO: Verificar se precisa inscrever para mudanças do pai (caso effectiveDataPath já esteja definido)
    if (this.effectiveDataPath && !this._hasSubscribedToParentSelection) {
      this.checkAndSubscribeToParentSelection();
    }
  }

  /**
   * NOVO: Verifica e inscreve grade filha em aba para mudanças de seleção do pai
   * Chamado tanto no ngOnInit quanto no ngOnChanges quando effectiveDataPath é definido
   */
  private checkAndSubscribeToParentSelection(): void {
    const effectivePathForCheck = this.effectiveDataPath ?? this.props?.dataPath;
    const effectivePathParts = (effectivePathForCheck || '').split('.').filter(Boolean).length;
    const isNestedTabGrid = this.isTabGrid && effectivePathParts > 2;

    if (isNestedTabGrid && !this._hasSubscribedToParentSelection) {
      this._hasSubscribedToParentSelection = true;
      this.subscribeToParentSelectionChange();
    }
  }

  /**
   * Extrai e atualiza propriedades a partir de props
   */
  private updateFromProps(): void {
    if (!this.props) {
      return;
    }

    this.columns = this.props.columns || [];
    // Grade dentro de aba pode receber effectiveDataPath (ex: response.magalu.channels) do canvas
    this.dataPath = this.effectiveDataPath ?? this.props?.dataPath;
    this.enablePagination = this.props.enablePagination ?? true;
    this.pageSize = this.props.pageSize || 10;
    this.enableFilters = this.props.enableFilters ?? false;
    this.enableSorting = this.props.enableSorting ?? true;
    this.enableCRUD = this.props.enableCRUD ?? false;
    this.selectionMode = this.props.selectionMode || 'none';
    this.previewMode = this.props.previewMode ?? false;
    this.label = this.props.label ?? '';
    //  Master-Detail
    this.enableMasterDetail = this.props.enableMasterDetail ?? false;
    this.masterDetailFormPanel = this.props.masterDetailFormPanel;
    this.masterDetailFormPanels = this.props.masterDetailFormPanels;
    // View Mode
    this.viewMode = this.props.viewMode ?? 'inline';
    this.drawerWidth = this.props.drawerWidth ?? 40;
    this.setupInitialAutoAddSuppression();

    // Linhas visíveis → scroll vertical
    this.maxVisibleRows = this.props.maxVisibleRows || 0;
    this.updateScrollConfig();

    // ✅ Limpar dados antigos
    this.originalData = [];
    this.displayData = [];
    
    // ✅ Gerar dados fake se em preview mode
    if (this.previewMode && this.columns.length > 0) {
      this.generatePreviewData();
    } else {
      // Re-aplicar dados se já existirem
      this.refreshData();
    }
    
    this.cdr.markForCheck();
  }

  /**
   * Path efetivo: usa currentSelectionSubject quando a seleção atual é aninhada e bate com nosso path base.
   * Para grade dentro de aba (isTabGrid), infere path aninhado a partir dos dados quando ainda não há seleção aninhada (ex: após injetar JSON).
   */
  private getEffectiveDataPath(): string | undefined {
    const basePath = this.effectiveDataPath ?? this.props?.dataPath;
    if (!basePath) return undefined;

    // 1) currentSelectionSubject tem path aninhado que estende nosso basePath → usar
    const sel = this.dataSyncService.getCurrentSelection();
    if (sel?.arrayPath && sel.arrayPath.split('.').filter(Boolean).length > 2 &&
        sel.arrayPath.startsWith(basePath + '.')) {
      return sel.arrayPath;
    }

    // 2) CORREÇÃO: Usar _lastEffectivePath como fallback.
    //    Quando o usuário edita o formulário da aba filha, updateCurrentItemField chama
    //    updateValue() → dataStore$.next() → subscribers (incluindo esta grade) rodam
    //    SINCRONAMENTE — mas currentSelectionSubject só é atualizado DEPOIS (linha 482
    //    de data-sync.service). Resultado: currentSelection ainda tem o path da grade
    //    principal, e o check acima falha. _lastEffectivePath preserva o path correto
    //    da última vez que loadNestedData rodou com sucesso.
    if (this._lastEffectivePath &&
        this._lastEffectivePath.split('.').filter(Boolean).length > 2 &&
        this._lastEffectivePath.startsWith(basePath + '.')) {
      return this._lastEffectivePath;
    }

    const basePathParts = basePath.split('.').filter(Boolean).length;
    // Lista principal em aba: canvas já passou effectiveDataPath de 2 partes -> não inferir firstKey (evitar virar nested)
    if (this.effectiveDataPath != null && basePathParts === 2) {
      return basePath;
    }
    // Grade dentro de aba: ao injetar JSON ainda não há seleção aninhada; inferir path do primeiro array no item pai
    // CORREÇÃO: Nunca inferir path aninhado se effectiveDataPath já foi passado (evita virar nested e chamar loadNestedData)
    if (this.isTabGrid && basePathParts <= 2 && this.effectiveDataPath == null) {
      const data = this.dataBindingService.getCurrentData();
      if (data) {
        const parentArray = this.dataBindingService.resolvePath(data, basePath);
        if (Array.isArray(parentArray) && parentArray.length > 0) {
          const main = this.dataSyncService.getMainSelection();
          const parentIndex = main.index >= 0 && main.arrayPath === basePath ? main.index : 0;
          const parentItem = parentArray[parentIndex];
          const firstKey = parentItem ? this.findFirstArrayKey(parentItem) : null;
          if (firstKey) {
            return basePath + '.' + firstKey;
          }
        }
      }
    }
    return basePath;
  }

  /**
   * Inscreve para mudanças nos dados
   */
  private subscribeToDataChanges(): void {
    const basePath = this.effectiveDataPath ?? this.props?.dataPath;
    const basePathParts = (basePath || '').split('.').filter(Boolean).length;
    const canBeNestedParent = basePathParts <= 2;

    // ✅ Escuta dados globais do DataBindingService
    // CORREÇÃO: Ignorar durante edição de célula para evitar re-renderização e perda de foco
    this.dataBindingService.getData$().pipe(
      takeUntil(this.destroy$),
      filter(data => data !== null),
      filter(() => !this.dataSyncService.isCellEditing)
    ).subscribe(data => {
      // Evitar loops: se já estamos processando uma atualização, ignorar
      if (this._isProcessingUpdate) {
        return;
      }
      const effectivePath = this.getEffectiveDataPath();
      // CORREÇÃO: Usar basePath original para determinar se é grade principal
      const originalBasePath = this.effectiveDataPath ?? this.props?.dataPath;
      const baseSegments = (originalBasePath || '').split('.').filter(Boolean).length;
      const effectiveSegments = effectivePath ? effectivePath.split('.').filter(Boolean).length : 0;

      // CORREÇÃO: Grade é nested quando effectivePath tem 3+ segmentos E:
      //   - basePath original TEM 3+ segmentos (grade filha com path explícito), OU
      //   - isTabGrid é true (grade em aba cujo effectivePath foi estendido pela seleção)
      // Sem a segunda condição, grades filhas em aba com basePath de 2 segmentos (ex: "response.data")
      // usavam loadData() que resolve do JSON global, não do item pai. Resultado: edições no
      // formulário da aba filha (que escrevem em response.data[0].identifiers[0].type) nunca
      // apareciam na grade porque loadData() buscava de "response.data" (array principal).
      const isNested = effectiveSegments > 2 && (baseSegments > 2 || this.isTabGrid);

      if (!isNested) {
        this.loadData(data);
      } else {
        // CORREÇÃO: Grades aninhadas PRECISAM recarregar quando getData$ emite
        // O DataBindingService faz deep clone, então as referências mudam
        // Removida a otimização que assumia mutação in-place
        const main = this.dataSyncService.mainSelectionSubject.value;
        const basePath = this.effectiveDataPath ?? this.props?.dataPath;
        // Path do array PAI (ex: response.magalu) quando effectivePath é aninhado (ex: response.magalu.channels)
        const parentArrayPath = effectivePath.split('.').filter(Boolean).length > 2
          ? effectivePath.split('.').slice(0, 2).join('.')
          : (main.arrayPath && main.arrayPath === basePath ? main.arrayPath : basePath);
        let parentArray = this.dataBindingService.resolvePath(data, parentArrayPath);
        // Se o pai não é array (ex: response.magalu = { items: [...] }), usar o primeiro array do objeto para a grade principal carregar
        if (!Array.isArray(parentArray) && parentArray != null && typeof parentArray === 'object') {
          const firstKey = this.findFirstArrayKey(parentArray);
          if (firstKey) parentArray = parentArray[firstKey];
        }
        if (!Array.isArray(parentArray) || parentArray.length === 0) return;
        const parentIndex = main.index >= 0 && main.arrayPath === parentArrayPath ? main.index : 0;
        const parentItem = parentArray[parentIndex];
        if (parentItem) {
          const skipFormFill = this.dataSyncService.isUpdatingFromForm;
          
          this.loadNestedData(parentItem, effectivePath, skipFormFill);
        }
      }
    });

    // Só inscrever em MasterDetail grades que não estão em aba: grades em aba (isTabGrid) já carregam via getData$();
    // se inscreverem, ao selecionar linha na principal recebem o evento e chamam loadNestedData(selectedRow), recarregando
    // com array errado (piscar e células vazias).
    // CORREÇÃO: Também não inscrever se effectiveDataPath foi passado (grade em aba com path definido pelo canvas)
    const shouldSubscribeMasterDetail = canBeNestedParent &&
                                         !this.isTabGrid &&
                                         !this.effectiveDataPath;
    if (shouldSubscribeMasterDetail) {
      this.subscribeToMasterDetail();
    }

    // NOTA: A inscrição para mudanças do pai foi movida para checkAndSubscribeToParentSelection()
    // que é chamado no ngOnInit e ngOnChanges quando effectiveDataPath está disponível
  }

  /**
   * NOVO: Inscreve grade filha em aba para mudanças de seleção do pai
   * Quando o usuário seleciona outra linha na grade principal, esta grade recarrega seus dados
   */
  private subscribeToParentSelectionChange(): void {
    const myPath = this.effectiveDataPath ?? this.props?.dataPath;

    if (!myPath) {
      return;
    }

    // Determinar path do pai (ex: response.magalu a partir de response.magalu.channels)
    const pathParts = myPath.split('.').filter(Boolean);
    if (pathParts.length < 3) {
      return;
    }
    const parentPath = pathParts.slice(0, 2).join('.');

    // Armazenar último índice do pai para detectar mudança REAL de linha
    let lastParentIndex = -1;

    this.dataSyncService.mainSelectionSubject.pipe(
      takeUntil(this.destroy$),
      filter(sel => sel.arrayPath === parentPath && sel.index >= 0),
      // CORREÇÃO: Ignorar se estamos editando célula OU se é apenas atualização do item (mesmo índice)
      filter(() => !this.dataSyncService.isCellEditing),
      // CORREÇÃO: Verificar se o índice REALMENTE mudou (não apenas o item)
      filter(sel => sel.index !== lastParentIndex)
    ).subscribe(parentSelection => {
      lastParentIndex = parentSelection.index;

      // Buscar item pai atualizado do store
      const data = this.dataBindingService.getCurrentData();
      if (!data) return;

      const parentArray = this.dataBindingService.resolvePath(data, parentPath);
      if (!Array.isArray(parentArray)) return;

      const parentItem = parentArray[parentSelection.index];
      // CORREÇÃO: Resetar timestamp de debounce para permitir reload imediato
      this._lastNestedLoadTimestamp = 0;
      // CORREÇÃO: Chamar loadNestedData com o item pai correto
      // Isso vai recarregar a grade filha E preencher o formulário
      this.loadNestedData(parentItem, myPath, false);
    });
  }

  /**
   * Verifica se esta grid é aninhada usando o dataPath ORIGINAL (não inferido).
   * Uma grade é aninhada apenas se seu dataPath configurado tem 3+ segmentos.
   */
  isNestedGrid(): boolean {
    // CORREÇÃO: Usar dataPath original, não o path inferido
    // Isso evita que a grade principal (response.magalu) seja considerada nested
    // quando getEffectiveDataPath() infere um path aninhado
    const basePath = this.effectiveDataPath ?? this.props?.dataPath;
    if (!basePath) return false;
    return basePath.split('.').filter(Boolean).length > 2;
  }
  
  /**
   * Inscreve para eventos de Master-Detail
   */
  private subscribeToMasterDetail(): void {
    const myGridId = this.props?.field || 'unknown-grid';
    
  
    
    this.masterDetailService.selectedRow$.pipe(
      takeUntil(this.destroy$),
      filter(state => state.selectedRow !== null),
      // CRÍTICO: Grids aninhadas só devem processar eventos da GRID PRINCIPAL
      filter(state => {
        // CORREÇÃO: Ignorar se esta grid tem effectiveDataPath definido (grid em aba)
        if (this.effectiveDataPath) {
          return false;
        }

        const isMyOwnEvent = state.sourceGridId === myGridId;
        if (isMyOwnEvent) {
          return false;
        }
        const sourceArrayPath = state.arrayPath || '';
        const myPath = this.getEffectiveDataPath() ?? this.dataPath ?? '';
        // Não processar quando o evento é da MESMA lista (ex: outra instância da grade principal);
        // senão esta grade chama loadNestedData(selectedRow) e recarrega com o primeiro array da linha (células vazias).
        if (sourceArrayPath === myPath) {
          return false;
        }
        const sourcePathParts = sourceArrayPath.split('.');
        const isFromMainGrid = sourcePathParts.length <= 2;
        if (!isFromMainGrid) {
          return false;
        }
        return true;
      }),
      distinctUntilChanged((prev, curr) => 
        // Comparar apenas o selectedRow para evitar reprocessamento desnecessário
        JSON.stringify(prev.selectedRow) === JSON.stringify(curr.selectedRow)
      )
    ).subscribe(state => {
      this.loadNestedData(state.selectedRow);
    });
  }
  
  /**
   * Carrega dados de um item pai selecionado (Master-Detail)
   * @param parentItem Item pai (ex: linha da grade principal)
   * @param effectivePath Path efetivo do array filho (ex: response.magalu.channels); quando não informado usa this.dataPath
   * @param skipFormFill Se true, atualiza apenas a grade (dados + seleção) sem chamar fillNestedFormDirectly (evita loop ao editar no form)
   */
  private loadNestedData(parentItem: any, effectivePath?: string, skipFormFill?: boolean): void {
    let pathToUse = effectivePath ?? this.dataPath;

    // CORREÇÃO: Se parentItem é null/undefined, limpar dados da grade (linha pai sem dados filhos)
    if (!parentItem) {
      this.originalData = [];
      this.displayData = [];
      this.selectedRows.clear();
      this._lastNestedParentIndex = -1;
      this.cdr.markForCheck();
      return;
    }

    // CORREÇÃO: Throttle com trailing edge — máximo 1 chamada a cada 100ms,
    // mas garante que a ÚLTIMA chamada dentro da janela seja processada.
    // Sem trailing edge, edições rápidas no formulário podiam ser perdidas.
    const now = Date.now();
    if ((now - this._lastNestedLoadTimestamp) < 100) {
      // Agendar trailing edge (cancela anterior se existir)
      if (this._pendingNestedLoadTimer) clearTimeout(this._pendingNestedLoadTimer);
      this._pendingNestedLoadTimer = setTimeout(() => {
        this._pendingNestedLoadTimer = null;
        this.loadNestedData(parentItem, effectivePath, skipFormFill);
      }, 100);
      return;
    }
    if (this._pendingNestedLoadTimer) {
      clearTimeout(this._pendingNestedLoadTimer);
      this._pendingNestedLoadTimer = null;
    }
    this._lastNestedLoadTimestamp = now;

    // Quando chamado do MasterDetail (sem effectivePath) e path tem só 2 partes (ex: response.magalu),
    // inferir o array filho pelo primeiro array no parentItem (ex: channels)
    const parts = (pathToUse || '').split('.').filter(Boolean);
    if (parts.length <= 2) {
      const firstArrayKey = this.findFirstArrayKey(parentItem);
      if (firstArrayKey) {
        pathToUse = (pathToUse || '') + (pathToUse ? '.' : '') + firstArrayKey;
      } else {
        // CORREÇÃO: Se não encontrou array filho, limpar dados (linha pai sem array aninhado)
        this.originalData = [];
        this.displayData = [];
        this.selectedRows.clear();
        this._lastNestedParentIndex = -1;
        this.cdr.markForCheck();
        return;
      }
    }
    if (!pathToUse) return;

    const partsToUse = pathToUse.split('.');
    const nestedPath = partsToUse.length >= 3 ? partsToUse.slice(2).join('.') : '';

    const resolved = this.dataBindingService.resolvePath(parentItem, nestedPath);
    const dataArray = Array.isArray(resolved) ? resolved : [];

    // CORREÇÃO: Detectar se o item PAI mudou (ex: selecionou outra linha na grade principal)
    // Quando o pai muda, SEMPRE resetar seleção e preencher formulário, mesmo que structureChanged=false
    const mainSel = this.dataSyncService.mainSelectionSubject.value;
    const parentChanged = mainSel.index !== this._lastNestedParentIndex;
    this._lastNestedParentIndex = mainSel.index;

    const previousLength = this.originalData.length;
    const structureChanged = dataArray.length !== previousLength;
    const currentSelection = this.dataSyncService.currentSelectionSubject.value;
    const hasExistingSelection = this.selectedRows.size > 0 &&
                                  currentSelection.arrayPath === pathToUse &&
                                  currentSelection.index >= 0;

    this._lastEffectivePath = effectivePath ?? (pathToUse.split('.').filter(Boolean).length > 2 ? pathToUse : undefined);

    // CORREÇÃO: Sempre atualizar dados visualmente quando estrutura não mudou e pai não mudou
    // Removida a verificação de hasExistingSelection pois impedia atualização quando outra grade estava selecionada
    if (!structureChanged && !parentChanged) {
      // CORREÇÃO: Criar novas referências para objetos internos para Angular detectar mudanças
      this.originalData = dataArray.map(item => ({ ...item }));
      // CORREÇÃO: Usar _skipTableRecreation para atualização síncrona (mesmo padrão do loadData).
      // Sem isso, applyFiltersAndSort destrói/recria a tabela assincronamente (tableVisible=false→setTimeout→true),
      // causando flash visual e perda de seleção por referência.
      this._skipTableRecreation = true;
      this.applyFiltersAndSort();
      this._skipTableRecreation = false;
      
      // Manter seleção se esta grade tiver seleção válida
      if (hasExistingSelection && currentSelection.index < dataArray.length) {
        const updatedRow = this.displayData[currentSelection.index];
        if (updatedRow) {
          this.selectedRows.clear();
          this.selectedRows.add(updatedRow);
        }
      }
      
      // CORREÇÃO: Restaurar seleção visual mesmo sem hasExistingSelection.
      // Quando o usuário edita um campo no formulário da aba filha, o currentSelectionSubject
      // pode ter sido sobrescrito por outra grade (é compartilhado), então hasExistingSelection=false.
      // Mas selectedRows ainda tem referência antiga. Precisamos atualizar para a nova referência
      // do displayData para que a linha continue visualmente selecionada.
      if (!hasExistingSelection && this.selectedRows.size > 0) {
        const oldSelected = Array.from(this.selectedRows)[0];
        if (oldSelected) {
          const matchIdx = this.displayData.findIndex(row =>
            JSON.stringify(row) === JSON.stringify(oldSelected)
          );
          if (matchIdx >= 0) {
            this.selectedRows.clear();
            this.selectedRows.add(this.displayData[matchIdx]);
          }
        }
      }
      
      this.cdr.detectChanges(); // Forçar detecção de mudanças
      return;
    }

    // CORREÇÃO: Criar novas referências para objetos internos para Angular detectar mudanças
    this.originalData = dataArray.map(item => ({ ...item }));
    this.applyFiltersAndSort();
    this.selectedRows.clear();

    if (dataArray.length > 0 && this.selectionMode !== 'none') {
      // CORREÇÃO: Se o pai mudou, NÃO considerar seleção anterior como válida
      // A seleção era do item pai ANTERIOR e não se aplica ao novo
      const hasActiveSelection = !parentChanged &&
                                 currentSelection.arrayPath === pathToUse &&
                                 currentSelection.index >= 0 &&
                                 currentSelection.index < dataArray.length;

      // CORREÇÃO: Quando _selectLastRowOnNextLoad está ativo (linha recém-adicionada via onAddRow),
      // ignorar hasActiveSelection e ir direto para o path "selecionar primeira/última linha".
      // Sem isso, hasActiveSelection=true (a seleção anterior index=0 é válida no array que cresceu)
      // faz o sistema re-selecionar a linha ANTIGA em vez da recém-adicionada.
      if (hasActiveSelection && !this._selectLastRowOnNextLoad) {
        const rowToSelect = this.displayData[currentSelection.index];
        if (rowToSelect) {
          this.selectedRows.clear();
          this.selectedRows.add(rowToSelect);
          this.cdr.markForCheck();
          // CORREÇÃO: Incluir targetNodeIds para isolamento de painel (Regra 7)
          // Sem isso, campos com mesmo dataPath em OUTRAS abas recebem valores errados
          const targetNodeIds = this.isNestedGrid() ? this.getFormFieldNodeIdsForNestedGrid() : undefined;
          this.dataSyncService.currentSelectionSubject.next({
            item: rowToSelect,
            index: currentSelection.index,
            arrayPath: pathToUse,
            targetNodeIds
          });
          if (!skipFormFill && this.masterDetailFormPanel) {
            this.fillNestedFormDirectly(rowToSelect, this.masterDetailFormPanel);
          }
        }
        return;
      }

      // Selecionar primeira linha (pai mudou OU não há seleção ativa)
      // Se _selectLastRowOnNextLoad=true, selecionar a ÚLTIMA linha (recém-adicionada)
      const selectLast = this._selectLastRowOnNextLoad;
      this._selectLastRowOnNextLoad = false;
      setTimeout(() => {
        if (this.displayData.length > 0) {
          const targetIndex = selectLast ? this.displayData.length - 1 : 0;
          const targetRow = this.displayData[targetIndex];
          this.selectedRows.clear();
          this.selectedRows.add(targetRow);
          this.cdr.markForCheck();
          // CORREÇÃO: Incluir targetNodeIds para isolamento de painel (Regra 7)
          // Sem isso, campos com mesmo dataPath em OUTRAS abas recebem valores errados
          const targetNodeIds = this.isNestedGrid() ? this.getFormFieldNodeIdsForNestedGrid() : undefined;
          this.dataSyncService.currentSelectionSubject.next({
            item: targetRow,
            index: targetIndex,
            arrayPath: pathToUse,
            targetNodeIds
          });
          // CORREÇÃO: Sempre preencher formulário ao selecionar linha
          if (!skipFormFill && this.masterDetailFormPanel) {
            this.fillNestedFormDirectly(targetRow, this.masterDetailFormPanel);
          }
        }
      }, 150);
    } else {
      // Array vazio - limpar seleção e formulário
      if (currentSelection.arrayPath === pathToUse) {
        this.dataSyncService.currentSelectionSubject.next({
          item: null,
          index: -1,
          arrayPath: pathToUse
        });
      }
      if (this.masterDetailFormPanel) {
        this.clearNestedForm(this.masterDetailFormPanel);
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Limpa os campos de formulário associado a uma grade aninhada
   * Chamado quando a grade filha não tem dados (linha pai sem array filho)
   */
  private clearNestedForm(panelId: string): void {
    // CORREÇÃO: Usar resolveNestedFormPanel para determinar o painel correto
    const effectivePanelId = this.resolveNestedFormPanel() || panelId;
    
    const allNodes = (this.builderService?.currentState.nodes ?? []);
    const idsUnderPanel = (this.builderService?.getNodeIdsUnderPanel ?? emptyIds)(allNodes, effectivePanelId);
    const formFields = allNodes.filter(node =>
      this.isFormField(node.type) && idsUnderPanel.has(node.id)
    );

    if (formFields.length === 0) {
      return;
    }

    // CORREÇÃO: Usar emitFieldSync com targetNodeIds para limpar SOMENTE campos deste painel.
    const targetNodeIds = new Set(formFields.map(f => f.id));
    
    formFields.forEach(field => {
      const fieldName = this.extractFieldNameForForm(field);
      this.dataSyncService.emitFieldSync(fieldName, '', 'nested_grid_clear', targetNodeIds);
    });
  }

  /**
   * Re-aplica dados atuais (útil quando colunas mudam)
   */
  private refreshData(): void {
    const currentData = this.dataBindingService.getCurrentData();
    if (currentData) {
      this.loadData(currentData);
    }
  }

  /**
   * Calcula a configuração de scroll baseada em maxVisibleRows.
   * Quando maxVisibleRows > 0, define a altura Y do scroll para limitar linhas visíveis.
   *
   * Cálculo da altura por linha (nz-table size='middle' + estilos do data-grid):
   *   - td padding: 10px top + 10px bottom = 20px
   *   - Conteúdo (texto/input): ~20px
   *   - Células editáveis adicionam: .editable-cell 2px + .cell-input 4px ≈ 12px
   *   - Border bottom: 1px
   *   - Total estimado: ~48-53px por linha
   *
   * Cálculo empírico (confirmado com testes reais):
   *   - Altura real por linha com nzBordered + nzSize=middle: ~62px
   *     (td padding 10+10, conteúdo ~20px, borders, spacing NG-ZORRO interno)
   *   - Scrollbar horizontal consome ~14px de espaço vertical dentro do container
   *   - Fórmula: (maxVisibleRows × 62) + 14
   */
  private updateScrollConfig(): void {
    if (this.maxVisibleRows > 0) {
      const rowHeight = 62;
      const scrollbarBuffer = 14; // Espaço da scrollbar horizontal
      const scrollY = (this.maxVisibleRows * rowHeight) + scrollbarBuffer;
      this.scrollConfig = { x: 'max-content', y: `${scrollY}px` };
    } else {
      this.scrollConfig = { x: 'max-content' };
    }
  }

  /**
   * ✅ NOVO: Gera dados fake para preview
   */
  private generatePreviewData(): void {
    if (!this.columns || this.columns.length === 0) {
      return;
    }

    let fakeData: any[];

    if (this.previewData && this.previewData.length > 0) {
      // Usar dados fornecidos (ex: do Smart Column Builder com JSON colado)
      fakeData = this.previewData;
    } else {
      // Gerar dados fake baseados nas colunas
      fakeData = this.fakeDataGenerator.generateFakeData(this.columns, this.pageSize);
    }

    // Aplicar dados fake
    this.originalData = fakeData;
    this.applyFiltersAndSort();
    this.cdr.markForCheck();
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this._initialAutoAddSuppressTimer) {
      clearTimeout(this._initialAutoAddSuppressTimer);
      this._initialAutoAddSuppressTimer = null;
    }
    if (this._pendingLoadDataTimer) {
      clearTimeout(this._pendingLoadDataTimer);
      this._pendingLoadDataTimer = null;
    }
    if (this._pendingNestedLoadTimer) {
      clearTimeout(this._pendingNestedLoadTimer);
      this._pendingNestedLoadTimer = null;
    }
  }
  
  /**
   * Encontra a primeira chave que contém um array dentro de um objeto
   * @param obj Objeto para procurar
   * @returns Nome da chave que contém o array, ou null
   */
  private findFirstArrayKey(obj: any): string | null {
    if (!obj || typeof obj !== 'object') return null;
    
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        return key;
      }
    }
    
    return null;
  }
  
  /**
   * Carrega dados e aplica path
   */
  private loadData(data: any): void {
    // Evitar recarregamentos muito frequentes (throttle leve)
    const now = Date.now();
    if ((now - this._lastLoadTimestamp) < DataGridComponent.MIN_LOAD_INTERVAL_MS) {
      // CORREÇÃO: Cancelar timer pendente anterior antes de agendar novo (evita cascata de setTimeouts)
      if (this._pendingLoadDataTimer) {
        clearTimeout(this._pendingLoadDataTimer);
      }
      this._pendingLoadDataTimer = setTimeout(() => {
        this._pendingLoadDataTimer = null;
        this.loadData(data);
      }, DataGridComponent.MIN_LOAD_INTERVAL_MS);
      return;
    }
    this._lastLoadTimestamp = now;

    this._lastEffectivePath = undefined;
    // Resolver path para obter array de dados
    let dataArray: any[];
    if (this.dataPath) {
      let resolved = this.dataBindingService.resolvePath(data, this.dataPath);

      // ⭐ NOVO: Se path não existe (null/undefined), inicializar array vazio
      if (resolved === null || resolved === undefined) {
        dataArray = [];
      }
      // ⭐ NOVO: Se não for array, tentar encontrar o primeiro array dentro do objeto
      else if (!Array.isArray(resolved) && typeof resolved === 'object') {
        const firstArrayKey = this.findFirstArrayKey(resolved);
        if (firstArrayKey) {
          resolved = resolved[firstArrayKey];
          dataArray = Array.isArray(resolved) ? resolved : [];
        } else {
          dataArray = [];
        }
      } else {
        dataArray = Array.isArray(resolved) ? resolved : [];
      }
    } else {
      dataArray = Array.isArray(data) ? data : [];
    }

    // CORREÇÃO: Verificar se o array realmente mudou (evitar loops quando apenas propriedades internas mudam)
    const newDataLength = dataArray.length;
    const oldDataLength = this.originalData.length;
    const structureChanged = newDataLength !== oldDataLength;

    // CORREÇÃO: Resetar hasAutoSelected APENAS quando é uma nova injeção de dados (oldDataLength era 0)
    // NÃO resetar quando estamos adicionando linhas (oldDataLength > 0)
    // Isso evita que autoSelectFirstRow seja chamado ao adicionar nova linha
    if (structureChanged && newDataLength > 0 && oldDataLength === 0) {
      this.hasAutoSelected = false;
      this.hasAutoOpenedFormOnInitialAdd = false;
    }

    // Se não há dados, limpar flag para próxima inicialização
    if (newDataLength === 0) {
      this.hasAutoOpenedFormOnInitialAdd = false;
    }

    // CORREÇÃO FASE 1.2: Atualizar currentSelectionSubject com item atualizado do array
    // Isso mantém o formulário sincronizado após edição na grade
    // CORREÇÃO: NÃO emitir durante edição de célula (evita loops)
    if (!this.dataSyncService.isCellEditing) {
      const currentSelection = this.dataSyncService.currentSelectionSubject.value;
      const myPath = this.effectiveDataPath ?? this.props?.dataPath;
      
      if (currentSelection.arrayPath === myPath && currentSelection.index >= 0 && currentSelection.index < dataArray.length) {
        // Re-emitir com o item atualizado do store
        const updatedItem = dataArray[currentSelection.index];
        if (updatedItem && updatedItem !== currentSelection.item) {
          this.dataSyncService.currentSelectionSubject.next({
            ...currentSelection,
            item: updatedItem
          });
        }
      }
    }

    // CORREÇÃO: Se estrutura não mudou e temos seleção, atualizar referências forçando detecção de mudanças.
    // O DataBindingService faz deep clone, então precisamos criar nova referência para Angular detectar mudanças.
    const shouldMaintainSelection = !structureChanged && oldDataLength > 0 && this.selectedRows.size > 0;
    const currentIndex = this.dataSyncService.mainSelectionSubject.value.index;

    if (shouldMaintainSelection) {
      // CORREÇÃO: Criar cópia profunda dos objetos para garantir que Angular detecte mudanças
      this.originalData = dataArray.map(item => ({ ...item }));
      
      // CORREÇÃO CRÍTICA: Pular destruição/recriação da tabela para shouldMaintainSelection.
      // Antes, applyFiltersAndSort destruía a tabela (tableVisible=false) e recriava com setTimeout(0).
      // Isso causava:
      //   1. displayData era atualizado ASSINCRONAMENTE (dentro do setTimeout)
      //   2. A restauração de seleção (logo abaixo) usava this.displayData[currentIndex] que apontava
      //      para o displayData ANTIGO → selectedRows tinha referências velhas → seleção perdida
      //   3. Destruição dos inputs disparava (blur) → onCellBlur → novo timer → re-emissão getData$ → ciclo
      //   4. Flash visual na tela
      // Com skipTableRecreation=true, displayData é atualizado SINCRONAMENTE. A seleção é restaurada
      // com referências CORRETAS do novo displayData. Sem blur events. Sem flash.
      this._skipTableRecreation = true;
      this.applyFiltersAndSort();
      this._skipTableRecreation = false;
      
      // Restaurar seleção com nova referência (agora displayData já foi atualizado sincronamente!)
      if (currentIndex >= 0 && currentIndex < this.displayData.length) {
        const updatedRow = this.displayData[currentIndex];
        this.selectedRows.clear();
        this.selectedRows.add(updatedRow);
      }
      
      this.cdr.detectChanges();
      return;
    }

    // Estrutura mudou ou primeira carga - reprocessamento completo
    this.originalData = dataArray.slice();
    this.applyFiltersAndSort();

    this.cdr.detectChanges();

    // CORREÇÃO: Se _selectLastRowOnNextLoad está ativo (linha recém-adicionada via onAddRow),
    // selecionar a ÚLTIMA linha em vez de restaurar a seleção anterior.
    // Timing: addGridRow chama addArrayItem → dataStore$.next() → loadData (SÍNCRONO) →
    // neste ponto mainSelectionSubject AINDA tem a seleção ANTIGA.
    // Após addArrayItem retornar, addGridRow emite mainSelectionSubject.next() com a nova linha,
    // mas loadData já terminou. Sem esta correção, o checkbox fica na linha antiga.
    if (this._selectLastRowOnNextLoad && !this.isNestedGrid() && this.displayData.length > 0) {
      this._selectLastRowOnNextLoad = false;
      const lastIndex = this.displayData.length - 1;
      const lastRow = this.displayData[lastIndex];
      this.selectedRows.clear();
      this.selectedRows.add(lastRow);
      this.cdr.markForCheck();
      // NÃO return aqui — deixar hasAutoSelected/autoSelectFirstRow serem controlados abaixo
    } else {
      // Restaurar seleção visual quando dados são recarregados (ex: injeção) e esta grid é a principal com seleção ativa
      // CORREÇÃO: Não emitir setSelectedRow aqui para evitar loops - apenas restaurar seleção visual
      if (!this.isNestedGrid() && this.displayData.length > 0 && this.selectionMode !== 'none') {
        const main = this.dataSyncService.mainSelectionSubject.value;
        if (main.arrayPath === this.dataPath && main.index >= 0 && main.index < this.displayData.length) {
          const restoredRow = this.displayData[main.index];
          this.selectedRows.clear();
          this.selectedRows.add(restoredRow);
          this.cdr.markForCheck();
        }
      }
    }

    //  Auto-selecionar primeira linha APENAS UMA VEZ na primeira carga
    // CORREÇÃO: Verificar se já existe seleção válida no mainSelectionSubject
    // Se existir, não auto-selecionar (evita sobrescrever seleção feita por addGridRow)
    const currentMainSelection = this.dataSyncService.mainSelectionSubject.value;
    const hasValidMainSelection = currentMainSelection.arrayPath === this.dataPath &&
                                   currentMainSelection.index >= 0 &&
                                   currentMainSelection.index < dataArray.length;

    // Auto-add inicial disparado pelo canvas: abrir formulário automaticamente em flip/drawer.
    const autoAddedOnInit = this.dataSyncService.consumeInitialAutoAdd(this.props?.field);
    if (!this.isNestedGrid() &&
        this.props?.autoAddFirstRow === true &&
        this.enableMasterDetail &&
        (this.viewMode === 'flip' || this.viewMode === 'drawer') &&
        !this.hasAutoOpenedFormOnInitialAdd &&
        autoAddedOnInit &&
        newDataLength > 0) {
      this.hasAutoOpenedFormOnInitialAdd = true;
      // Abre o formulário no próximo tick para evitar "pisca" da face da grade.
      setTimeout(() => this.openFormForCurrentViewMode('adding'), 0);
    }

    if (!this.isNestedGrid() &&
        dataArray.length > 0 &&
        this.selectionMode !== 'none' &&
        !this.hasAutoSelected &&
        !hasValidMainSelection) {
      this.hasAutoSelected = true;
      setTimeout(() => {
        this.autoSelectFirstRow();
      }, 300);
    }
  }
  
  /**
   * Auto-seleciona a primeira linha (útil para Master-Detail)
   */
  private autoSelectFirstRow(): void {
    if (this.displayData.length > 0) {
      const firstRow = this.displayData[0];
      
      // ✅ SEMPRE selecionar visualmente a primeira linha (grids principais E aninhadas)
      this.selectedRows.clear();
      this.selectedRows.add(firstRow);
      
      // Emitir evento local
      this.rowSelected.emit(firstRow);
      
      // ✅ MODIFICADO: Todas as grids emitem, mas apenas para seus próprios painéis
      const gridId = this.props?.field || 'unknown-grid';
      const arrayPath = this.props?.dataPath || '';
      const rowIndex = 0;
      
      // 🔧 Determinar panelIds de forma isolada
      let panelIds: string[] | undefined;
      
      if (this.isNestedGrid()) {
        // GRIDS ANINHADAS: usar APENAS masterDetailFormPanels
        if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
          panelIds = this.masterDetailFormPanels;
        } else if (this.masterDetailFormPanel) {
          panelIds = [this.masterDetailFormPanel];
        }
      } else {
        // GRID PRINCIPAL: pode combinar ambos
        if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
          if (this.masterDetailFormPanel && !this.masterDetailFormPanels.includes(this.masterDetailFormPanel)) {
            panelIds = [this.masterDetailFormPanel, ...this.masterDetailFormPanels];
          } else {
            panelIds = this.masterDetailFormPanels;
          }
        } else if (this.masterDetailFormPanel) {
          panelIds = [this.masterDetailFormPanel];
        }
      }
      
      // ✅ CRÍTICO: Na AUTO-SELEÇÃO, grids aninhadas NÃO emitem eventos
      // Isso evita interferência com o formulário principal ao carregar
      if (!this.isNestedGrid()) {
        // CORREÇÃO: Emitir mainSelectionSubject ANTES de setSelectedRow
        // Isso evita que initializeMasterDetailListener emita duplicata
        // (a verificação compara com mainSelectionSubject.value que já estará atualizado)
        this.dataSyncService.mainSelectionSubject.next({
          item: firstRow,
          index: rowIndex,
          arrayPath: arrayPath
        });
        this.dataSyncService.currentSelectionSubject.next({
          item: firstRow,
          index: rowIndex,
          arrayPath: arrayPath
        });
        
        // CORREÇÃO: Sempre emitir evento para o MasterDetailService, mesmo sem panelIds explícitos
        // O canvas usa getMasterFormPanelIds() para descobrir os painéis a preencher
        this.masterDetailService.setSelectedRow(
          gridId,
          firstRow,
          this.masterDetailFormPanel,
          false,
          arrayPath,
          rowIndex,
          panelIds // Pode ser undefined, canvas tratará
        );
      }

      this.cdr.markForCheck();
    }
  }

  private openFormForCurrentViewMode(mode: 'adding' | 'editing'): void {
    if (this.viewMode === 'flip') {
      if (!this.isFlipEditing) {
        this.enterFlipMode(mode);
      }
      // Após ativar flip, a própria condição de flip já esconde a face da grade.
      this.clearInitialAutoAddSuppression();
      return;
    }
    if (this.viewMode === 'drawer') {
      if (!this.isDrawerOpen) {
        this.openDrawer();
      }
      // Drawer tem animação de entrada; manter supressão até o início da abertura.
      setTimeout(() => this.clearInitialAutoAddSuppression(), 180);
      return;
    }
    this.clearInitialAutoAddSuppression();
  }

  private setupInitialAutoAddSuppression(): void {
    const shouldSuppress =
      !this.isNestedGrid() &&
      this.props?.autoAddFirstRow === true &&
      this.enableMasterDetail &&
      (this.viewMode === 'flip' || this.viewMode === 'drawer');

    if (!shouldSuppress) {
      this.clearInitialAutoAddSuppression();
      return;
    }

    this.suppressGridFaceForInitialAutoAdd = true;
    if (this._initialAutoAddSuppressTimer) {
      clearTimeout(this._initialAutoAddSuppressTimer);
    }
    // Fallback para evitar travar oculto se auto-add não ocorrer por algum motivo.
    this._initialAutoAddSuppressTimer = setTimeout(() => {
      this.clearInitialAutoAddSuppression();
      this.cdr.markForCheck();
    }, 2000);
  }

  private clearInitialAutoAddSuppression(): void {
    this.suppressGridFaceForInitialAutoAdd = false;
    if (this._initialAutoAddSuppressTimer) {
      clearTimeout(this._initialAutoAddSuppressTimer);
      this._initialAutoAddSuppressTimer = null;
    }
  }
  
  /**
   * Aplica filtros e ordenação aos dados
   */
  private applyFiltersAndSort(): void {
    // R5: No modo spreadsheet, sempre usar _skipTableRecreation para evitar destruição
    if (this.shouldSkipRecreationForSpreadsheet) {
      this._skipTableRecreation = true;
    }

    // CORREÇÃO: Criar cópias profundas para garantir novas referências
    let filtered = this.originalData.map(item => ({ ...item }));

    // Aplicar filtros
    if (this.enableFilters && Object.keys(this.columnFilters).length > 0) {
      filtered = this.applyFilters(filtered);
    }

    // Aplicar ordenação
    if (this.sortColumn) {
      filtered = this.applySorting(filtered);
    }

    // Calcular paginação
    this.totalPages = Math.ceil(filtered.length / this.pageSize);

    // Aplicar paginação
    let newData: any[];
    if (this.enablePagination) {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      // CORREÇÃO: Criar nova referência do array com cópias dos objetos
      newData = filtered.slice(startIndex, endIndex).map(item => ({ ...item }));
    } else {
      // CORREÇÃO: Criar nova referência do array
      newData = filtered.map(item => ({ ...item }));
    }

    // CORREÇÃO CRÍTICA: Forçar re-renderização do nz-table
    // Comparar se houve mudança real nos dados para decidir se precisa forçar refresh
    const oldJson = JSON.stringify(this.displayData);
    const newJson = JSON.stringify(newData);
    
    if (oldJson !== newJson) {
      if (this._skipTableRecreation) {
        // CORREÇÃO: Quando shouldMaintainSelection=true, NÃO destruir/recriar a tabela.
        // A destruição assíncrona (tableVisible=false→setTimeout→true) causa:
        //   1. Perda de seleção (selectedRows tem referências do displayData ANTIGO)
        //   2. Blur events nos inputs destruídos → onCellBlur → re-emissão de getData$ → ciclo
        //   3. Flash visual desnecessário
        // Basta atualizar displayData sincronamente. O trackBy(index) garante que Angular
        // reutilize os <tr> existentes e os bindings ([ngModel]) se atualizem corretamente.
        this.displayData = newData;
      } else {
        // Primeira carga ou mudança estrutural: destruir e recriar a tabela
        // para forçar nz-table a re-renderizar completamente
        this.tableVisible = false;
        this.cdr.detectChanges();
        
        setTimeout(() => {
          this.displayData = newData;
          this.tableVisible = true;
          this.cdr.detectChanges();
        }, 0);
      }
    } else {
      // Mesmo conteúdo - apenas atualizar referência
      this.displayData = newData;
    }
  }
  
  /**
   * Aplica filtros aos dados
   */
  private applyFilters(data: any[]): any[] {
    return data.filter(row => {
      for (const [columnField, filterValue] of Object.entries(this.columnFilters)) {
        if (!filterValue) continue;
        
        const column = this.columns.find(c => c.field === columnField);
        if (!column) continue;
        
        const cellValue = this.resolveColumnValue(row, column);
        const cellValueStr = String(cellValue).toLowerCase();
        const filterValueStr = filterValue.toLowerCase();
        
        if (!cellValueStr.includes(filterValueStr)) {
          return false;
        }
      }
      return true;
    });
  }
  
  /**
   * Aplica ordenação aos dados
   */
  private applySorting(data: any[]): any[] {
    if (!this.sortColumn) return data;
    
    const column = this.columns.find(c => c.field === this.sortColumn);
    if (!column) return data;
    
    return [...data].sort((a, b) => {
      const valueA = this.resolveColumnValue(a, column);
      const valueB = this.resolveColumnValue(b, column);
      
      // Tratamento de null/undefined
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;
      
      // Comparação baseada no tipo
      let comparison = 0;
      
      if (column.type === 'number') {
        comparison = Number(valueA) - Number(valueB);
      } else if (column.type === 'date') {
        comparison = new Date(valueA).getTime() - new Date(valueB).getTime();
      } else {
        comparison = String(valueA).localeCompare(String(valueB));
      }
      
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }
  
  /**
   * TrackBy function para ngFor de linhas
   * CORREÇÃO: Usar APENAS o índice para evitar destruição/recriação do <tr> durante edição de célula.
   * Se usássemos JSON.stringify(row), qualquer mutação local (via setRowValue no onCellEdit)
   * faria o Angular considerar a linha como "nova", destruir o <tr> e o input perderia foco.
   * A re-renderização quando dados mudam de fontes externas (formulário) já é garantida
   * pelo mecanismo tableVisible=false/true em applyFiltersAndSort.
   */
  /**
   * Coluna pelo campo, e não pelo índice.
   *
   * Os `*ngFor` de coluna rodam uma vez POR LINHA: numa grade de 40 colunas e
   * 50 linhas são 2.000 iterações recriando `<td>` a cada mudança. Pelo campo,
   * reordenar colunas move o `<td>` em vez de recriá-lo.
   */
  trackByColumnField(index: number, column: DataGridColumn): string {
    return column?.field ?? String(index);
  }

  /** Opção pela chave: a lista vem de consulta e a ordem pode mudar. */
  trackByOptionKey(index: number, option: { key?: unknown }): string {
    return option?.key === undefined ? String(index) : String(option.key);
  }

  trackByPage(index: number, page: number): number {
    return page;
  }

  trackByRowIndex(index: number, row: any): number {
    return index;
  }

  /**
   * Resolve o valor de uma célula usando o dataPath da coluna
   */
  resolveColumnValue(row: any, column: DataGridColumn): any {
    if (!column.dataPath) {
      // Fallback: usar field name diretamente
      return row[column.field];
    }
    
    // Resolver path relativo à linha
    const value = this.dataBindingService.resolvePath(row, column.dataPath);
    
    // Aplicar formatter se especificado
    if (column.formatter && value !== null && value !== undefined) {
      return this.dataBindingService['applyTransform'](value, column.formatter);
    }
    
    return value;
  }
  
  /**
   * Manipulador de mudança de página
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyFiltersAndSort();
    this.cdr.markForCheck();
  }
  
  /**
   * Manipulador de mudança de ordenação
   */
  onSortChange(column: DataGridColumn): void {
    if (!column.sortable) return;
    
    if (this.sortColumn === column.field) {
      // Alternar direção
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // Nova coluna
      this.sortColumn = column.field;
      this.sortDirection = 'asc';
    }
    
    this.currentPage = 1; // Voltar para primeira página
    this.applyFiltersAndSort();
    this.cdr.markForCheck();
  }
  
  /**
   * Manipulador de mudança de filtro
   */
  onFilterChange(columnField: string, filterValue: string): void {
    if (filterValue) {
      this.columnFilters[columnField] = filterValue;
    } else {
      delete this.columnFilters[columnField];
    }
    
    this.currentPage = 1; // Voltar para primeira página
    this.applyFiltersAndSort();
    this.cdr.markForCheck();
  }

  /**
   * Filtro global que pesquisa em todas as colunas simultaneamente
   */
  onGlobalFilterChange(value: string): void {
    const searchTerm = value.toLowerCase().trim();
    
    if (!searchTerm) {
      // Se não há termo de busca, limpar filtros e mostrar todos os dados
      this.columnFilters = {};
      this.applyFiltersAndSort();
      this.cdr.markForCheck();
      return;
    }

    // Aplicar filtro global
    let filtered = this.originalData.filter(row => {
      // Verificar se algum campo contém o termo de busca
      return this.columns.some(column => {
        const cellValue = this.resolveColumnValue(row, column);
        if (cellValue === null || cellValue === undefined) return false;
        
        const cellValueStr = String(cellValue).toLowerCase();
        return cellValueStr.includes(searchTerm);
      });
    });

    // Aplicar ordenação se houver
    if (this.sortColumn) {
      filtered = this.applySorting(filtered);
    }

    // Calcular paginação
    this.totalPages = Math.ceil(filtered.length / this.pageSize);
    this.currentPage = 1; // Reset para página 1

    // Aplicar paginação
    if (this.enablePagination) {
      const startIndex = 0;
      const endIndex = this.pageSize;
      this.displayData = filtered.slice(startIndex, endIndex);
    } else {
      this.displayData = filtered;
    }

    this.cdr.markForCheck();
  }
  
  /**
   * Manipulador de seleção de linha
   * @param row - Objeto da linha selecionada
   * @param event - Evento do DOM
   * @param rowIndex - Índice da linha no displayData (0-based)
   */
  onRowSelect(row: any, event: Event, rowIndex?: number): void {
    if (this.selectionMode === 'none') return;

    if (this.selectionMode === 'single') {
      const actualRowIndex = rowIndex ?? this.displayData.indexOf(row);

      // CORREÇÃO: Se a mesma linha já está selecionada (ex: clicou em outra coluna da mesma linha),
      // NÃO re-emitir eventos. Isso evita que o formulário pisque/suma ao clicar entre colunas.
      const currentMainSel = this.dataSyncService.mainSelectionSubject.value;
      const isSameRow = !this.isNestedGrid() &&
                        currentMainSel.arrayPath === (this.props?.dataPath || '') &&
                        currentMainSel.index === actualRowIndex;

      if (isSameRow && this.selectedRows.has(row)) {
        // Mesma linha, não fazer nada (evita loop e form piscando)
        return;
      }

      this.selectedRows.clear();
      this.selectedRows.add(row);

      // Emitir evento local
      this.rowSelected.emit(row);

      const gridId = this.props?.field || 'unknown-grid';
      const arrayPath = this.props?.dataPath || '';

      // Determinar panelIds de forma isolada
      let panelIds: string[] | undefined;

      if (this.isNestedGrid()) {
        if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
          panelIds = this.masterDetailFormPanels;
        } else if (this.masterDetailFormPanel) {
          panelIds = [this.masterDetailFormPanel];
        }
      } else {
        if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
          if (this.masterDetailFormPanel && !this.masterDetailFormPanels.includes(this.masterDetailFormPanel)) {
            panelIds = [this.masterDetailFormPanel, ...this.masterDetailFormPanels];
          } else {
            panelIds = this.masterDetailFormPanels;
          }
        } else if (this.masterDetailFormPanel) {
          panelIds = [this.masterDetailFormPanel];
        }
      }

      // CRÍTICO: Grids aninhadas NÃO emitem eventos para evitar interferência
      if (!this.isNestedGrid()) {
        this.dataSyncService.mainSelectionSubject.next({
          item: row,
          index: actualRowIndex,
          arrayPath: arrayPath
        });
        this.dataSyncService.currentSelectionSubject.next({
          item: row,
          index: actualRowIndex,
          arrayPath: arrayPath
        });

        this.masterDetailService.setSelectedRow(
          gridId,
          row,
          this.masterDetailFormPanel,
          false,
          arrayPath,
          actualRowIndex,
          panelIds
        );

        // Modo drawer: mostrar painéis automaticamente ao selecionar linha
        if (this.viewMode === 'drawer' && this.enableMasterDetail) {
          this.openDrawer();
        }
      } else {
        // ✅ GRIDS ANINHADAS: Preencher formulário diretamente via DOM (sem MasterDetailService)
        const rowIndex = this.displayData.indexOf(row);
        const pathForSelection = this._lastEffectivePath ?? this.dataPath;
        const targetNodeIds = this.getFormFieldNodeIdsForNestedGrid();
        this.dataSyncService.currentSelectionSubject.next({
          item: row,
          index: rowIndex,
          arrayPath: pathForSelection,
          targetNodeIds
        });
        
        if (this.masterDetailFormPanel) {
          this.fillNestedFormDirectly(row, this.masterDetailFormPanel);
        }

        // Drawer mode em grids aninhadas: mostrar painéis automaticamente ao selecionar linha
        if (this.viewMode === 'drawer' && this.enableMasterDetail) {
          this.openDrawer();
        }
      }
    } else {
      // Multiple
      if (this.selectedRows.has(row)) {
        this.selectedRows.delete(row);
      } else {
        this.selectedRows.add(row);
      }
      
      // Para seleção múltipla, emitir array de linhas selecionadas
      this.rowSelected.emit(Array.from(this.selectedRows));
    }
    
    this.cdr.markForCheck();
  }

  /**
   *  NOVO: Abre o Master-Detail Panel manualmente
   */
  openMasterDetailPanel(): void {
    if (this.selectedRows.size === 0 || !this.enableMasterDetail) {
      return;
    }

    // Modo flip: entrar no modo edição em vez de abrir painel
    if (this.viewMode === 'flip') {
      this.enterFlipMode('editing');
      return;
    }

    // Modo drawer: mostrar painéis vinculados via evento ao canvas
    if (this.viewMode === 'drawer') {
      this.openDrawer();
      return;
    }

    // ⚠️ IMPORTANTE: Grids aninhadas NÃO devem emitir para Master-Detail
    // Isso evita que a seleção da grid principal seja perdida
    if (this.isNestedGrid()) {
      return;
    }

    // Pegar a primeira linha selecionada
    const selectedRow = Array.from(this.selectedRows)[0];
    
    //  Verificar se a linha selecionada tem dados
    if (!selectedRow) {
      return;
    }
    
    const gridId = this.props?.field || 'unknown-grid';
    
    // 🔧 Determinar panelIds de forma isolada
    let panelIds: string[] | undefined;
    
    if (this.isNestedGrid()) {
      // GRIDS ANINHADAS: usar APENAS masterDetailFormPanels
      if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
        panelIds = this.masterDetailFormPanels;
      } else if (this.masterDetailFormPanel) {
        panelIds = [this.masterDetailFormPanel];
      }
    } else {
      // GRID PRINCIPAL: pode combinar ambos
      if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
        if (this.masterDetailFormPanel && !this.masterDetailFormPanels.includes(this.masterDetailFormPanel)) {
          panelIds = [this.masterDetailFormPanel, ...this.masterDetailFormPanels];
        } else {
          panelIds = this.masterDetailFormPanels;
        }
      } else if (this.masterDetailFormPanel) {
        panelIds = [this.masterDetailFormPanel];
      }
    }

    //  Usar openPanel ao invés de setSelectedRow para forçar abertura
    this.masterDetailService.openPanel(
      gridId,
      selectedRow,
      this.masterDetailFormPanel,
      panelIds
    );
  }

  /**
   * Resolve os panelIds vinculados a esta grade (reutilizado por flip/drawer/openMasterDetail).
   * Se os painéis configurados não existem ou não têm campos, faz fallback para o painel pai da grid.
   */
  private getResolvedPanelIds(): string[] {
    let panelIds: string[] = [];
    if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
      if (this.masterDetailFormPanel && !this.masterDetailFormPanels.includes(this.masterDetailFormPanel)) {
        panelIds = [this.masterDetailFormPanel, ...this.masterDetailFormPanels];
      } else {
        panelIds = [...this.masterDetailFormPanels];
      }
    } else if (this.masterDetailFormPanel) {
      panelIds = [this.masterDetailFormPanel];
    }

    const allNodes = (this.builderService?.currentState.nodes ?? []);
    const validPanelIds = panelIds.filter(pid => {
      const panelNode = allNodes.find(n => n.id === pid && n.type === 'panel');
      if (!panelNode) return false;
      const idsUnderPanel = (this.builderService?.getNodeIdsUnderPanel ?? emptyIds)(allNodes, pid);
      return allNodes.some(n => this.isFormField(n.type) && idsUnderPanel.has(n.id));
    });

    if (validPanelIds.length > 0) return validPanelIds;

    const fallbackPanel = this.resolveGridParentPanel();
    return fallbackPanel ? [fallbackPanel] : panelIds;
  }

  private resolveGridParentPanel(): string | undefined {
    const allNodes = (this.builderService?.currentState.nodes ?? []);
    const myField = this.props?.field;
    if (!myField) return undefined;

    const gridNode = allNodes.find(n =>
      n.id === myField ||
      (n.props as any)?.field === myField ||
      (n.props as any)?.fieldName === myField
    );
    if (!gridNode?.parentId) return undefined;

    const parentPanel = gridNode.parentId;
    const idsUnderPanel = (this.builderService?.getNodeIdsUnderPanel ?? emptyIds)(allNodes, parentPanel);
    const hasFormFields = allNodes.some(n => this.isFormField(n.type) && idsUnderPanel.has(n.id));
    return hasFormFields ? parentPanel : undefined;
  }

  /**
   * Entra no modo flip (double-face): esconde a grade e mostra os painéis de formulário.
   * R3 mitigation: limpa isCellEditing global caso esteja true.
   */
  enterFlipMode(mode: 'adding' | 'editing'): void {
    if (this.viewMode !== 'flip') return;

    // R3: limpar flag global de edição de célula para não bloquear outros componentes
    if (this.dataSyncService.isCellEditing) {
      this.dataSyncService.setCellEditing(false);
    }

    this.flipEditingMode = mode;
    this.isFlipEditing = true;

    const gridId = this.props?.field || 'unknown-grid';
    const panelIds = this.getResolvedPanelIds();

    this.flipStateChanged.emit({ active: true, gridId, panelIds });
    this.cdr.markForCheck();
  }

  /**
   * Sai do modo flip: esconde os formulários e mostra a grade novamente.
   */
  exitFlipMode(): void {
    this.isFlipEditing = false;

    const gridId = this.props?.field || 'unknown-grid';
    const panelIds = this.getResolvedPanelIds();

    this.flipStateChanged.emit({ active: false, gridId, panelIds });
    this.cdr.markForCheck();
  }

  /**
   * Abre o drawer lateral (modo drawer): mostra os painéis de formulário em uma gaveta.
   */
  openDrawer(): void {
    if (this.viewMode !== 'drawer') return;
    this.isDrawerOpen = true;
    const gridId = this.props?.field || 'unknown-grid';
    const panelIds = this.getResolvedPanelIds();
    this.drawerStateChanged.emit({ active: true, gridId, panelIds, drawerWidth: this.drawerWidth });
    this.cdr.markForCheck();
  }

  /**
   * Fecha o drawer lateral.
   */
  closeDrawer(): void {
    this.isDrawerOpen = false;
    const gridId = this.props?.field || 'unknown-grid';
    const panelIds = this.getResolvedPanelIds();
    this.drawerStateChanged.emit({ active: false, gridId, panelIds, drawerWidth: this.drawerWidth });
    this.cdr.markForCheck();
  }
  
  /**
   * Ação "Editar" na coluna de CRUD: garante que a linha está selecionada e abre o modo adequado.
   */
  onEditRow(row: any, rowIndex: number): void {
    if (!this.selectedRows.has(row)) {
      this.onRowSelect(row, new Event('click'), rowIndex);
    }
    if (this.viewMode === 'flip') {
      this.enterFlipMode('editing');
    } else if (this.viewMode === 'drawer') {
      this.openDrawer();
    } else {
      this.openMasterDetailPanel();
    }
  }

  /**
   * Verifica se uma linha está selecionada
   */
  isRowSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  // ── Spreadsheet Mode ────────────────────────────────────────────

  /**
   * Verifica se uma célula específica está ativa para edição no modo spreadsheet.
   */
  isCellActive(rowIndex: number, colIndex: number): boolean {
    if (this.viewMode !== 'spreadsheet' || !this.activeCell) return false;
    return this.activeCell.rowIndex === rowIndex && this.activeCell.colIndex === colIndex;
  }

  /**
   * Ativa uma célula para edição no modo spreadsheet.
   * R3: Usa flag local _isSpreadsheetEditing em vez do global isCellEditing.
   */
  activateCell(rowIndex: number, colIndex: number): void {
    if (this.viewMode !== 'spreadsheet') return;
    this.activeCell = { rowIndex, colIndex };
    this._isSpreadsheetEditing = true;
    this.cdr.markForCheck();

    // Focar o input após o Angular renderizar
    setTimeout(() => {
      const input = document.querySelector(
        `.spreadsheet-cell-input[data-row="${rowIndex}"][data-col="${colIndex}"]`
      ) as HTMLElement;
      if (input) input.focus();
    }, 0);
  }

  /**
   * Desativa a célula ativa no modo spreadsheet.
   */
  deactivateCell(): void {
    this.activeCell = null;
    this._isSpreadsheetEditing = false;
    this.cdr.markForCheck();
  }

  /**
   * Navega entre células no modo spreadsheet via teclado.
   */
  onSpreadsheetKeydown(event: KeyboardEvent, rowIndex: number, colIndex: number): void {
    if (this.viewMode !== 'spreadsheet') return;

    switch (event.key) {
      case 'Tab': {
        event.preventDefault();
        const nextCol = colIndex + 1;
        if (nextCol < this.columns.length) {
          this.activateCell(rowIndex, nextCol);
        } else if (rowIndex + 1 < this.displayData.length) {
          this.activateCell(rowIndex + 1, 0);
        } else {
          // Tab na última célula da última linha: adicionar nova linha
          if (this.enableCRUD && this.dataPath) {
            this.onAddRow();
            setTimeout(() => {
              this.activateCell(this.displayData.length - 1, 0);
            }, 300);
          }
        }
        break;
      }
      case 'Enter': {
        event.preventDefault();
        const nextRow = rowIndex + 1;
        if (nextRow < this.displayData.length) {
          this.activateCell(nextRow, colIndex);
        }
        break;
      }
      case 'Escape': {
        event.preventDefault();
        this.deactivateCell();
        break;
      }
      case 'ArrowUp': {
        if (rowIndex > 0) {
          event.preventDefault();
          this.activateCell(rowIndex - 1, colIndex);
        }
        break;
      }
      case 'ArrowDown': {
        if (rowIndex + 1 < this.displayData.length) {
          event.preventDefault();
          this.activateCell(rowIndex + 1, colIndex);
        }
        break;
      }
    }
  }

  /**
   * R5: No modo spreadsheet, applyFiltersAndSort SEMPRE usa _skipTableRecreation.
   */
  private get shouldSkipRecreationForSpreadsheet(): boolean {
    return this.viewMode === 'spreadsheet' && this._isSpreadsheetEditing;
  }

  // ── Fim Spreadsheet Mode ────────────────────────────────────────
  
  /**
   * CORREÇÃO: Chamado quando o usuário foca em uma célula editável
   * Define isCellEditing = true para evitar re-renderização durante a digitação
   */
  onCellFocus(): void {
    this.dataSyncService.setCellEditing(true);
  }

  /**
   * CORREÇÃO: Chamado quando o usuário sai de uma célula editável
   * Aguarda um tempo antes de limpar a flag para permitir processamento
   */
  onCellBlur(): void {
    // CORREÇÃO: Cancelar timer anterior para evitar re-emissões redundantes de getData$.
    // Quando o usuário sai de uma célula e brevemente foca outra (evento transitório),
    // ambos os blur geram timers. Sem cancelamento, AMBOS re-emitem getData$, causando
    // processamento duplicado desnecessário.
    if (this._cellBlurTimerId) {
      clearTimeout(this._cellBlurTimerId);
      this._cellBlurTimerId = null;
    }
    
    // Aguardar um tempo para permitir que as atualizações sejam processadas
    // antes de limpar a flag
    this._cellBlurTimerId = setTimeout(() => {
      this._cellBlurTimerId = null;
      
      // CORREÇÃO: Verificar se NÃO houve novo focus (evitar limpar flag se usuário clicou em outra célula)
      if (this.dataSyncService.isCellEditing) {
        // Verificar se o foco atual está dentro desta grade
        const activeElement = document.activeElement;
        const gridElement = document.querySelector(`[data-grid-id="${this.props?.field}"]`);
        const isStillInGrid = gridElement && gridElement.contains(activeElement);
        
        if (isStillInGrid) {
          return;
        }
      }
      
      this.dataSyncService.setCellEditing(false);
      
      // CORREÇÃO: Forçar re-emissão de getData$ para que grades e formulários
      // recebam os dados atualizados. Durante isCellEditing=true, as emissões
      // eram bloqueadas/ignoradas. Agora que a edição terminou, precisamos
      // garantir que todos os subscribers recebam o estado atual.
      const currentData = this.dataBindingService.getCurrentData();
      if (currentData) {
        this.dataBindingService.setData(currentData);
      }
    }, 300);
  }

  /**
   *  NOVO: Manipula edição de célula (com debounce)
   * Envia para o Subject que aplica debounce de 300ms
   * @param row Linha sendo editada
   * @param column Coluna sendo editada
   * @param newValue Novo valor
   */
  onCellEdit(row: any, column: DataGridColumn, newValue: any, displayIndex?: number): void {
    // Atualizar valor local imediatamente (para feedback visual)
    const fieldPath = column.dataPath || column.field;
    this.setRowValue(row, fieldPath, newValue);
    
    // Enviar para o Subject com debounce para sincronização com JSON global
    // CORREÇÃO: Passar displayIndex para evitar uso de indexOf (que falha com cópias de objetos)
    const idx = displayIndex ?? this.displayData.indexOf(row);
    this.cellEditSubject$.next({ row, column, value: newValue, displayIndex: idx });
  }

  /**
   * Processa a edição de célula após debounce
   * @param row Linha sendo editada
   * @param column Coluna sendo editada
   * @param newValue Novo valor
   */
  private processCellEdit(row: any, column: DataGridColumn, newValue: any, displayIndex: number): void {
    // CORREÇÃO: Usar displayIndex diretamente, ajustando para paginação se necessário.
    // Antes usava originalData.indexOf(row) que retornava -1 porque displayData contém
    // cópias dos objetos (criadas por .map(item => ({ ...item })) em applyFiltersAndSort).
    let rowIndex: number;
    if (this.enablePagination) {
      rowIndex = (this.currentPage - 1) * this.pageSize + displayIndex;
    } else {
      rowIndex = displayIndex;
    }

    if (rowIndex === -1 || rowIndex >= this.originalData.length) {
      return;
    }

    // Sincronizar com o JSON global
    const fieldPath = column.dataPath || column.field;
    
    // CORREÇÃO CRÍTICA: Usar o path BASE (configurado) para a grade principal.
    // getEffectiveDataPath() pode retornar um path aninhado (3+ segmentos) baseado na
    // seleção ATUAL (ex: se o usuário estava em uma aba filha como "channels").
    // Isso fazia onGridCellChange escrever no path ERRADO:
    //   Ex: response.magalu[0].channels[0].title em vez de response.magalu[0].title
    // O subscribeToDataChanges já tem essa mesma proteção (baseSegments > 2 && effectiveSegments > 2).
    const originalBasePath = this.effectiveDataPath ?? this.props?.dataPath;
    const baseSegments = (originalBasePath || '').split('.').filter(Boolean).length;
    
    let gridDataPath: string;
    if (baseSegments <= 2) {
      // Grade principal: usar o path configurado, não o inferido
      // getEffectiveDataPath() poderia retornar ex: response.magalu.channels (3 segmentos)
      // se a seleção atual estiver em uma aba filha, causando escrita no path errado
      gridDataPath = originalBasePath || this.dataPath;
    } else {
      // Grade aninhada: usar o path efetivo (ex: response.magalu.channels)
      gridDataPath = this._lastEffectivePath ?? this.getEffectiveDataPath() ?? this.dataPath;
    }
    
    this.dataSyncService.onGridCellChange(rowIndex, fieldPath, newValue, gridDataPath);

    // CORREÇÃO: Emitir fieldSync$ para atualizar campos de formulário com mesmo dataPath
    // Usa um sourceNodeId sintético (grid_cell_...) que nunca vai colidir com nodeIds de campos de formulário
    const gridId = this.props?.field || 'unknown-grid';
    this.dataSyncService.emitFieldSync(fieldPath, newValue, `grid_cell_${gridId}`);

    // Para grids aninhadas: re-preencher o formulário com o item ATUALIZADO do store
    // O canvas.updateFormFieldsFromGlobalData() NÃO atualiza forms de grids filhas (isNestedForm=true lá)
    // então precisamos fazer aqui diretamente
    if (this.isNestedGrid() && this.masterDetailFormPanel && gridDataPath) {
      // Ler item atualizado diretamente do store (não de currentSelection que pode estar desatualizado)
      this.fillNestedFormDirectly(row, this.masterDetailFormPanel);
    }

    // Forçar atualização da view
    this.cdr.markForCheck();
  }
  
  /**
   * Define valor em um objeto usando path
   */
  private setRowValue(row: any, path: string, value: any): void {
    const parts = path.split('.');
    let current = row;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    current[parts[parts.length - 1]] = value;
  }
  
  /**
   * Retorna ícone de ordenação para uma coluna
   */
  getSortIcon(column: DataGridColumn): string {
    if (!column.sortable || this.sortColumn !== column.field) {
      return '';
    }
    
    return this.sortDirection === 'asc' ? 'arrow-up' : 'arrow-down';
  }
  
  /**
   * Retorna array de páginas para paginação
   */
  /** A lista de páginas, memoizada. Ver `_pagesCache`. */
  getPagesArray(): number[] {
    if (this._pagesCacheFor !== this.totalPages) {
      this._pagesCacheFor = this.totalPages;
      this._pagesCache = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }

    return this._pagesCache;
  }

  /**
   * O array de páginas, guardado entre chamadas.
   *
   * Antes, `getPagesArray()` criava um array NOVO a cada chamada — e ela é
   * chamada de dentro de um `*ngFor`, ou seja, a cada ciclo de detecção.
   * Identidade nova a cada ciclo faz o Angular desmontar e remontar a paginação
   * inteira sem que nada tenha mudado. É o padrão que o plano cita como origem
   * de metade dos travamentos do módulo antigo (`getGridCells`, 240 itens por
   * ciclo).
   */
  private _pagesCache: number[] = [];
  private _pagesCacheFor = -1;
  
  /**
   * Adiciona nova linha na grid
   */
  onAddRow(): void {
    // Na previa do construtor os dados sao LOCAIS: `generatePreviewData` escreve
    // direto em `originalData`, e o `data-sync` nao participa. Mandar a linha
    // para o servico ali gravaria num lugar que a grade nao le — e o clique
    // parece nao fazer nada, que era o relato.
    if (this.previewMode) {
      this.addPreviewRow();
      return;
    }

    if (!this.dataPath) {
      console.error('dataPath não configurado para adicionar linha');
      return;
    }
    const gridId = this.props?.field || 'unknown-grid';
    let panelIds: string[] | undefined;
    if (this.masterDetailFormPanels && this.masterDetailFormPanels.length > 0) {
      if (this.masterDetailFormPanel && !this.masterDetailFormPanels.includes(this.masterDetailFormPanel)) {
        panelIds = [this.masterDetailFormPanel, ...this.masterDetailFormPanels];
      } else {
        panelIds = this.masterDetailFormPanels;
      }
    } else if (this.masterDetailFormPanel) {
      panelIds = [this.masterDetailFormPanel];
    }

    // Coletar defaultValues dos BuilderNodes que pertencem aos painéis master-detail
    const defaultValues = this.collectDefaultValuesFromFormNodes(panelIds);

    // Flag para que loadNestedData selecione a última linha (recém-adicionada) em vez da primeira
    this._selectLastRowOnNextLoad = true;

    this.dataSyncService.addGridRow(
      this.dataPath,
      undefined,
      this.columns,
      gridId,
      this.masterDetailFormPanel,
      panelIds,
      defaultValues
    );

    // Modo flip: após adicionar, virar para o formulário
    if (this.viewMode === 'flip') {
      setTimeout(() => this.enterFlipMode('adding'), 200);
    }
    // Modo drawer: após adicionar, mostrar os painéis vinculados
    if (this.viewMode === 'drawer') {
      setTimeout(() => this.openDrawer(), 200);
    }
  }

  /**
   * Acrescenta uma linha em branco à lista da prévia.
   *
   * Uma chave por coluna, e não um objeto vazio: sem as chaves, a linha
   * desenha células vazias sem largura e a tabela "pula" ao acrescentar.
   */
  private addPreviewRow(): void {
    const linha: Record<string, any> = {};

    for (const coluna of this.columns) {
      const chave = coluna?.dataPath || coluna?.field;

      if (chave) {
        linha[chave] = '';
      }
    }

    this.originalData = [...this.originalData, linha];
    this.applyFiltersAndSort();
    this.cdr.markForCheck();
  }

  /**
   * Coleta valores default dos campos do formulário (BuilderNodes) associados aos painéis desta grade.
   * Retorna um mapa de dataPath -> defaultValue para ser aplicado ao novo item.
   */
  private collectDefaultValuesFromFormNodes(panelIds?: string[]): Record<string, any> {
    const defaults: Record<string, any> = {};
    if (!panelIds || panelIds.length === 0) return defaults;

    const allNodes = (this.builderService?.currentState.nodes ?? []);

    // Filtrar nodes que pertencem a algum dos painéis master-detail
    const formNodes = allNodes.filter(n => {
      if (!n.parentId) return false;
      // Verificar se o parentId é um dos painéis, ou se é filho de um row dentro do painel
      return this.isNodeInsidePanels(n, allNodes, panelIds);
    });

    formNodes.forEach(node => {
      const props = node.props as any;
      const dataPath = props.dataPath || props.binding;
      if (!dataPath) return;

      let defaultValue = props.defaultValue;

      // Para dropdowns/selects, verificar opção marcada como isDefault
      if ((node.type === 'dropdown' || node.type === 'select') &&
          (defaultValue === undefined || defaultValue === null) &&
          props.options && Array.isArray(props.options)) {
        const defaultOption = props.options.find((opt: any) => opt.isDefault);
        if (defaultOption) {
          defaultValue = defaultOption.value;
        }
      }

      if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        defaults[dataPath] = defaultValue;
      }
    });

    return defaults;
  }

  /**
   * Verifica recursivamente se um node está dentro de algum dos painéis informados
   * (considerando rows intermediários).
   */
  private isNodeInsidePanels(node: any, allNodes: readonly any[], panelIds: string[]): boolean {
    let current = node;
    while (current.parentId) {
      if (panelIds.includes(current.parentId)) return true;
      current = allNodes.find(n => n.id === current.parentId);
      if (!current) return false;
    }
    return false;
  }
  
  /**
   * Remove linha da grid
   */
  onDeleteRow(row: any, rowIndex: number, event: MouseEvent): void {
    event.stopPropagation();
    
    if (!confirm('Deseja realmente excluir este item?')) {
      return;
    }
    
    const pathToUse = this._lastEffectivePath ?? this.getEffectiveDataPath() ?? this.dataPath;
    if (!pathToUse) {
      console.error('dataPath não configurado para remover linha');
      return;
    }
    
    const realIndex = this.enablePagination 
      ? (this.currentPage - 1) * this.pageSize + rowIndex
      : rowIndex;
    
    this.dataSyncService.removeGridRow(pathToUse, realIndex);
    
    // A grid será automaticamente atualizada via getData$() subscription
  }
  
  /**
   * ✅ NOVO: Preenche formulário de grid aninhada diretamente via DOM
   * Evita usar MasterDetailService para não interferir com o formulário principal
   */
  private fillNestedFormDirectly(rowData: any, panelId: string): void {
    setTimeout(() => {
      // CORREÇÃO: Usar resolveNestedFormPanel para determinar o painel correto
      const effectivePanelId = this.resolveNestedFormPanel() || panelId;
      
      const allNodes = (this.builderService?.currentState.nodes ?? []);
      const idsUnderPanel = (this.builderService?.getNodeIdsUnderPanel ?? emptyIds)(allNodes, effectivePanelId);
      const formFields = allNodes.filter(node =>
        this.isFormField(node.type) && idsUnderPanel.has(node.id)
      );
      
      if (formFields.length === 0) {
        return;
      }
      
      // CORREÇÃO: Usar emitFieldSync com targetNodeIds para atualizar SOMENTE campos deste painel.
      // Sem targetNodeIds, campos da grade principal com mesmo dataPath também seriam atualizados.
      const targetNodeIds = new Set(formFields.map(f => f.id));
      
      formFields.forEach(field => {
        const fieldName = this.extractFieldNameForForm(field);
        const value = this.dataBindingService.resolvePath(rowData, fieldName);
        
        // CORREÇÃO: Emitir fieldSync para TODOS os campos, incluindo null/undefined.
        // Sem isso, ao trocar para uma linha com campo vazio, o formulário mantém o valor
        // da linha anterior (o campo nunca é limpo).
        const safeValue = (value !== undefined && value !== null) ? value : '';
        this.dataSyncService.emitFieldSync(fieldName, safeValue, 'nested_grid_fill', targetNodeIds);
      });
    }, 100);
  }
  
  /**
   * CORREÇÃO: Retorna os IDs de todos os campos de formulário associados a esta grade aninhada.
   * Usado para passar targetNodeIds ao currentSelectionSubject.next() e fillNestedFormDirectly,
   * garantindo isolamento de painel (Regra 7) — campos de outras abas com mesmo dataPath
   * NÃO são atualizados.
   */
  private getFormFieldNodeIdsForNestedGrid(): Set<string> {
    const effectivePanelId = this.resolveNestedFormPanel() || this.masterDetailFormPanel;
    if (!effectivePanelId) return new Set();

    const allNodes = (this.builderService?.currentState.nodes ?? []);
    const idsUnderPanel = (this.builderService?.getNodeIdsUnderPanel ?? emptyIds)(allNodes, effectivePanelId);
    const formFields = allNodes.filter(n => this.isFormField(n.type) && idsUnderPanel.has(n.id));

    // Também incluir campos de masterDetailFormPanels (configuração plural, se existir)
    if (this.masterDetailFormPanels?.length > 0) {
      for (const panelId of this.masterDetailFormPanels) {
        if (panelId === effectivePanelId) continue; // Já incluído acima
        const panelIds = (this.builderService?.getNodeIdsUnderPanel ?? emptyIds)(allNodes, panelId);
        const panelFields = allNodes.filter(n => this.isFormField(n.type) && panelIds.has(n.id));
        panelFields.forEach(f => formFields.push(f));
      }
    }

    const result = new Set(formFields.map(f => f.id));

    return result;
  }

  /**
   * Resolve o painel correto para formulários vinculados à grade.
   * Se o masterDetailFormPanel configurado não contém campos de formulário
   * (ex: aponta para um painel inexistente), faz fallback para o painel pai da grid.
   * Funciona tanto para grids aninhadas quanto para grids principais.
   */
  private resolveNestedFormPanel(): string | undefined {
    const configuredPanel = this.masterDetailFormPanel;
    if (!configuredPanel) {
      return this.resolveGridParentPanel();
    }
    
    const allNodes = (this.builderService?.currentState.nodes ?? []);
    
    if (this.isNestedGrid()) {
      const fallback = this.resolveGridParentPanel();
      if (fallback) return fallback;
    }
    
    const panelNode = allNodes.find(n => n.id === configuredPanel && n.type === 'panel');
    if (panelNode) {
      const idsUnderPanel = (this.builderService?.getNodeIdsUnderPanel ?? emptyIds)(allNodes, configuredPanel);
      const hasFormFields = allNodes.some(n => this.isFormField(n.type) && idsUnderPanel.has(n.id));
      if (hasFormFields) return configuredPanel;
    }
    
    return this.resolveGridParentPanel() || configuredPanel;
  }

  /**
   * Verifica se o tipo é um campo de formulário
   */
  private isFormField(type: string): boolean {
    const formFieldTypes = ['text', 'textarea', 'number', 'switch', 'checkbox', 'select', 'date', 'time', 'datetime', 'dropdown', 'search', 'tag', 'image', 'button'];
    return formFieldTypes.includes(type);
  }
  
  /**
   * Extrai o nome do campo para mapear com os dados
   */
  private extractFieldNameForForm(field: any): string {
    const props = field.props as any;
    return props?.dataPath || props?.field || props?.label?.toLowerCase().replace(/\s+/g, '_') || field.id;
  }
  
  /**
   * Define valor de um campo diretamente via DOM
   * Usa busca parcial do ID para encontrar o campo
   */
  private setFieldValueDirectly(fieldId: string, value: any, retryCount: number = 0): void {
    // Tentar encontrar pelo ID completo
    let container = document.querySelector(`[data-component-id="${fieldId}"]`);
    
    // Se não encontrar, tentar busca parcial (o ID pode ter sido modificado)
    if (!container) {
      // Extrair a parte principal do ID (sem o sufixo gerado)
      const idParts = fieldId.split('-field-');
      if (idParts.length > 1) {
        const baseId = idParts[0];
        container = document.querySelector(`[data-component-id*="${baseId}"]`);
      }
    }
    
    if (!container) {
      // Retry até 3 vezes com delay crescente
      if (retryCount < 3) {
        setTimeout(() => {
          this.setFieldValueDirectly(fieldId, value, retryCount + 1);
        }, 200 * (retryCount + 1));
        return;
      }
      // Campo não encontrado após tentativas (pode estar em aba não visível)
      return;
    }
    
    const inputElement = container.querySelector('input') as HTMLInputElement;
    const textareaElement = container.querySelector('textarea') as HTMLTextAreaElement;
    
    if (inputElement) {
      inputElement.value = String(value);
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
      inputElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (textareaElement) {
      textareaElement.value = String(value);
      textareaElement.dispatchEvent(new Event('input', { bubbles: true }));
      textareaElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ── Rich Column Types (Search / Dropdown) ──────────────────────────

  /**
   * Retorna o label de uma opção de dropdown a partir do valor armazenado
   */
  getDropdownLabel(value: any, column: DataGridColumn): string {
    if (value === null || value === undefined) return '';
    if (!column.options || column.options.length === 0) return String(value);
    const opt = column.options.find(o => o.value == value);
    return opt ? opt.label : String(value);
  }

  /**
   * Retorna o display label para um valor de search usando cache.
   * Se não estiver em cache, retorna o valor bruto e dispara busca em background.
   */
  resolveSearchDisplay(value: any, column: DataGridColumn): string {
    if (value === null || value === undefined || value === '') return '';
    const cacheKey = column.field;
    if (!this.searchDisplayCache.has(cacheKey)) {
      this.searchDisplayCache.set(cacheKey, new Map());
    }
    const colCache = this.searchDisplayCache.get(cacheKey)!;
    
    // Suporte a showKey: value pode ser {key, value}
    const rawKey = typeof value === 'object' && value.key != null ? value.key : value;
    
    if (colCache.has(rawKey)) {
      return colCache.get(rawKey)!;
    }
    
    // Buscar label em background e cachear
    this.fetchSearchLabel(column, rawKey, colCache);
    return String(rawKey);
  }

  private fetchSearchLabel(column: DataGridColumn, keyValue: any, cache: Map<any, string>): void {
    if (!column.searchProperties?.entityName) return;
    const sp = column.searchProperties;
    const condition = `${sp.fields.keyField} = '${keyValue}'`;
    const ob = {
      entityName: sp.entityName,
      condition,
      showPresentation: false,
      showRelationshipData: false,
      showRelationship: false,
      showTableInformation: false,
    };
    this.httpService.post(ob, 'JsonBrokerServices/find', false, true, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        const list = response[sp.entityName];
        if (list && list.length > 0) {
          const label = list[0][sp.fields.valueField] || String(keyValue);
          cache.set(keyValue, column.showKey ? `${keyValue} - ${label}` : label);
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Inicializa o Subject de busca server-side para uma coluna search (chamado no template)
   */
  ensureSearchSubject(column: DataGridColumn): void {
    const key = column.field;
    if (this.searchColumnSubjects.has(key)) return;

    const subj = new Subject<string>();
    this.searchColumnSubjects.set(key, subj);
    this.searchColumnOptions.set(key, []);
    this.searchColumnLoading.set(key, false);

    subj.pipe(
      debounceTime(400),
      switchMap(term => {
        this.searchColumnLoading.set(key, true);
        this.cdr.markForCheck();
        return this.fetchSearchOptions(column, term);
      }),
      takeUntil(this.destroy$)
    ).subscribe(options => {
      this.searchColumnOptions.set(key, options);
      this.searchColumnLoading.set(key, false);
      this.cdr.markForCheck();
    });
  }

  onSearchColumnSearch(column: DataGridColumn, term: string): void {
    this.ensureSearchSubject(column);
    this.searchColumnSubjects.get(column.field)!.next(term);
  }

  private fetchSearchOptions(column: DataGridColumn, term: string): Observable<Array<{ key: any; value: string }>> {
    if (!column.searchProperties?.entityName) return of([]);
    const sp = column.searchProperties;
    let condition = '0=0';
    if (term) {
      condition = `(${sp.fields.keyField} LIKE '%${term}%' OR ${sp.fields.valueField} LIKE '%${term}%')`;
    }
    const ob = {
      entityName: sp.entityName,
      condition,
      showPresentation: false,
      showRelationshipData: false,
      showRelationship: false,
      showTableInformation: false,
    };
    return this.httpService.post(ob, 'JsonBrokerServices/find', false, true, true).pipe(
      switchMap((response: any) => {
        const list = response[sp.entityName];
        if (list && list.length > 0) {
          return of(list.map((item: any) => ({
            key: item[sp.fields.keyField],
            value: item[sp.fields.valueField]
          })));
        }
        return of([]);
      })
    );
  }

  /**
   * Chamado quando o usuário seleciona um item no search inline da célula
   */
  onSearchCellSelect(row: any, column: DataGridColumn, selectedValue: any, displayIndex: number): void {
    const fieldPath = column.dataPath || column.field;
    this.setRowValue(row, fieldPath, selectedValue);
    
    // Atualizar cache de display
    if (selectedValue != null) {
      const cacheKey = column.field;
      if (!this.searchDisplayCache.has(cacheKey)) {
        this.searchDisplayCache.set(cacheKey, new Map());
      }
      const rawKey = typeof selectedValue === 'object' && selectedValue.key != null ? selectedValue.key : selectedValue;
      const opts = this.searchColumnOptions.get(cacheKey) || [];
      const found = opts.find(o => o.key == rawKey);
      if (found) {
        this.searchDisplayCache.get(cacheKey)!.set(rawKey, column.showKey ? `${rawKey} - ${found.value}` : found.value);
      }
    }

    const idx = displayIndex ?? this.displayData.indexOf(row);
    this.cellEditSubject$.next({ row, column, value: selectedValue, displayIndex: idx });
  }

  getSearchColumnOptions(column: DataGridColumn): Array<{ key: any; value: string }> {
    return this.searchColumnOptions.get(column.field) || [];
  }

  isSearchColumnLoading(column: DataGridColumn): boolean {
    return this.searchColumnLoading.get(column.field) || false;
  }
}

